/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import axios, {
  AxiosInstance,
  AxiosError,
  AxiosRequestConfig,
  RawAxiosRequestHeaders,
} from "axios"
import { ReactNode } from "react"
import { BaseQueryFn } from "@reduxjs/toolkit/query"
import { NotificationArgsProps } from "@duxton-web/ui"
import { logApiError } from "@grab-food/web-monitoring-helpers"
import notification from "components/common/notification"
import {
  requestInterceptor,
  responseInterceptor,
} from "store/axios-interceptor"
import { actionCreator as globalActionCreator } from "store/duck/global"
import { AppState } from "store/store.type"
import { generateAxiosHeader } from "utils/api_helper"
import { storageHelper } from "utils/storage"
import { errorTargetDebugWhitelist } from "store/middlewares/constants"
import { apiBaseUrl } from "./constants"

/**
 * Single axios client instance for all API calls
 * Configured with interceptors matching Redux store for consistency
 * Uses full URLs (no baseURL) like Redux actions
 */
const createClient = (): AxiosInstance => {
  const axiosClient = axios.create({
    headers: generateAxiosHeader(),
    withCredentials: storageHelper.isCookieAuthEnabled(),
    responseType: "json",
  })

  // Apply the same interceptors used in Redux store
  const isDev = process.env.NODE_ENV === "development"
  requestInterceptor(axiosClient)
  responseInterceptor(axiosClient, isDev)

  return axiosClient
}

// Singleton client instance
export const apiClient = createClient()

/**
 * Helper to generate request headers with optional merchantID and merchantGroupID
 */
export const createRequestHeaders = (options?: {
  merchantID?: string
  merchantGroupID?: string
}): RawAxiosRequestHeaders => {
  const headers: RawAxiosRequestHeaders = {}

  if (options?.merchantID) {
    headers.merchantID = options.merchantID
  }
  if (options?.merchantGroupID) {
    headers.merchantGroupID = options.merchantGroupID
  }

  return headers
}

// ============================================================================
// RTK Query Custom Base Query (wraps existing axios client)
// ============================================================================

/** Shown in the error notification when `showError` is true. */
export interface ErrorNotificationContent {
  message: string
  description?: ReactNode
}

/**
 * Returns custom notification content for an error response.
 * Use this to override the message or description based on the response data.
 * To suppress the notification entirely, use `showError: false` instead.
 */
export type GetErrorNotificationContent = (args: {
  data: unknown
  defaultMessage: string
  trace?: string
  status?: number
}) => ErrorNotificationContent

export interface AxiosBaseQueryArgsExtraOptions {
  showError?: boolean
  /** Prevent error monitoring when the legacy endpoint explicitly opts out. */
  notTrackError?: boolean
  errorOptions?: Partial<NotificationArgsProps>
  showGlobalLoading?: boolean
  /**
   * When set, overrides default notification title/description (still merges
   * `errorOptions` from extraOptions). Use for API-specific error body shapes.
   */
  getErrorNotificationContent?: GetErrorNotificationContent
  /**
   * When true, shows a success notification after the request completes.
   * Use `successOptions` to customize the notification content.
   * For dynamic messages based on response data, handle inline in the component instead.
   */
  showSuccess?: boolean
  successOptions?: Partial<NotificationArgsProps>
}

export interface AxiosBaseQueryArgs {
  url: string
  method?: AxiosRequestConfig["method"]
  data?: AxiosRequestConfig["data"]
  params?: AxiosRequestConfig["params"]
  headers?: RawAxiosRequestHeaders
  /** Per-request axios timeout in milliseconds (optional). */
  timeout?: AxiosRequestConfig["timeout"]
  extraOptions?: AxiosBaseQueryArgsExtraOptions
}

export interface AxiosBaseQueryError {
  status?: number
  data?: unknown
  message?: string
  headers?: Record<string, string>
}

export interface AxiosBaseQueryMeta {
  headers: Record<string, string>
}

/**
 * Custom base query for RTK Query that wraps the existing axios client.
 * Supports optional per-endpoint error notifications via `extraOptions`.
 *
 * Usage in createApi:
 * ```
 * const api = createApi({
 *   baseQuery: axiosBaseQuery('https://example.com'),
 *   endpoints: (builder) => ({
 *     getData: builder.query({
 *       query: (params) => ({
 *         url: '/api/data',  // Relative URL - baseUrl is prepended automatically
 *         params,
 *         headers: createRequestHeaders({ merchantID }),
 *       }),
 *       // Show error notification for this endpoint (default: true)
 *       extraOptions: { showError: true },
 *     }),
 *     getSilent: builder.query({
 *       query: () => ({ url: '/api/silent' }),
 *       // Suppress error notification for this endpoint
 *       extraOptions: { showError: false },
 *     }),
 *   }),
 * })
 * ```
 */
export const axiosBaseQuery = (
  baseUrl: string = apiBaseUrl,
): BaseQueryFn<
  AxiosBaseQueryArgs,
  unknown,
  AxiosBaseQueryError,
  {},
  AxiosBaseQueryMeta
> => async (
  { url, method = "GET", data, params, headers, timeout, extraOptions },
  api,
) => {
  const {
    showError = true,
    notTrackError = false,
    errorOptions,
    showGlobalLoading = false,
    getErrorNotificationContent,
    showSuccess = false,
    successOptions,
  } = extraOptions ?? {}

  if (showGlobalLoading) {
    api.dispatch(globalActionCreator.showLoading())
  }

  try {
    const result = await apiClient({
      url: baseUrl + url,
      method,
      data,
      params,
      headers,
      ...(timeout !== undefined ? { timeout } : {}),
    })

    if (showSuccess) {
      const globalMessages = (api.getState() as AppState)?.global?.messages
      notification.success({
        message: globalMessages?.success ?? "Success",
        ...successOptions,
      })
    }

    const responseHeaders = Object.entries(result.headers ?? {}).reduce(
      (headerMap, [key, value]) => {
        if (typeof value === "string") {
          return { ...headerMap, [key]: value }
        }
        return headerMap
      },
      {} as Record<string, string>,
    )

    return { data: result.data, meta: { headers: responseHeaders } }
  } catch (axiosError) {
    const err = axiosError as AxiosError
    const error: AxiosBaseQueryError = {
      status: err.response?.status,
      data: err.response?.data,
      message: err.message,
      headers: err.response?.headers as Record<string, string> | undefined,
    }

    if (!notTrackError) {
      logApiError(baseUrl + url, error.status, axiosError)
    }

    if (showError) {
      const globalMessages = (api.getState() as AppState)?.global?.messages
      const defaultMessage = globalMessages?.error || "Something went wrong"
      const trace = error.headers?.["x-grabkit-grab-requestid"]

      const formatted = getErrorNotificationContent?.({
        data: error.data,
        defaultMessage,
        trace,
        status: error.status,
      })

      const errorData = error.data as Record<string, any> | undefined
      const forbiddenMessage =
        error.status === 403 && typeof errorData?.message === "string"
          ? errorData.message
          : undefined
      const message = forbiddenMessage ?? formatted?.message ?? defaultMessage
      const descriptionParts: string[] = []
      if (trace) descriptionParts.push(`trace=${trace}`)

      if (
        !formatted &&
        errorTargetDebugWhitelist?.includes(errorData?.target)
      ) {
        if (error.status) descriptionParts.push(`status=${error.status}`)
        if (errorData?.message)
          descriptionParts.push(`message=${errorData.message}`)
        if (errorData?.reason)
          descriptionParts.push(`reason=${errorData.reason}`)
        if (errorData?.target)
          descriptionParts.push(`target=${errorData.target}`)
      }

      const description =
        formatted?.description ??
        (descriptionParts.length > 0 ? descriptionParts.join(", ") : undefined)

      notification.error({
        message,
        description,
        key: message,
        ...errorOptions,
      })
    }

    return { error }
  } finally {
    if (showGlobalLoading) {
      api.dispatch(globalActionCreator.hideLoading())
    }
  }
}
